/**
 * K2 (ADR-111) — "Ngày tiệm nghỉ": đóng cửa cả chi nhánh theo ngày (Tết, lễ, sửa tiệm, mất điện), nằm trong Cấu hình →
 * Chi nhánh, ngay dưới giờ mở cửa. Chọn ngày → một nút → khách không đặt được ngày đó nữa.
 *
 * Cùng cách với ngày nghỉ của thợ (ADR-092): LƯU luôn, rồi liệt kê các lịch khách đã đặt vào những ngày đó (mỗi lịch là
 * một nút mở màn Lịch đúng ngày để gọi khách đổi ngày). Không hỏi xác nhận trước khi lưu; "mở lại" cũng không hỏi vì
 * đảo được bằng một chạm. Không có ô giờ: nghỉ nửa ngày là sửa giờ mở cửa, không phải việc của thẻ này.
 */
import { CalendarOff, X } from 'lucide-react'
import { useState } from 'react'
import { type ChiNhanhCH, cauHinh, type DotDongCua, type LichVuongNghi } from './api.ts'
import { ngayViet, ONgay } from './GoiVi.tsx'
import { CanhBaoLichVuong } from './NgayNghi.tsx'
import { T } from './text/vi.ts'

export function MucTiemNghi({
  branchId,
  ngayDong,
  onDoi,
  onLoi,
  onXong,
  onXemTrenLich,
}: {
  branchId: string
  ngayDong: readonly DotDongCua[]
  /** Máy chủ trả lại danh sách chi nhánh sau khi đóng / mở lại. */
  onDoi: (ds: ChiNhanhCH[]) => void
  onLoi: (e: unknown) => void
  onXong: (s: string) => void
  onXemTrenLich?: (ngay: string, chiNhanhId: string) => void
}) {
  const [tu, setTu] = useState('')
  const [den, setDen] = useState('')
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  /** Tăng sau mỗi lần lưu để ô ngày gõ-tay được dựng lại (ô tự giữ chữ đã gõ nên không tự xoá khi `giaTri` về rỗng). */
  const [lan, setLan] = useState(0)
  const [vuong, setVuong] = useState<{ ds: LichVuongNghi[]; tong: number } | null>(null)

  async function dong() {
    if (tu === '') return
    setDang(true)
    setVuong(null)
    try {
      const r = await cauHinh.dongCua(branchId, {
        tuNgay: tu,
        denNgay: den === '' ? tu : den,
        ...(lyDo.trim() === '' ? {} : { lyDo: lyDo.trim() }),
      })
      onDoi(r.chiNhanh)
      setTu('')
      setDen('')
      setLyDo('')
      setLan((n) => n + 1)
      const ds = r.lichVuong ?? []
      if (ds.length > 0) setVuong({ ds, tong: r.soLichVuong ?? ds.length })
      else onXong(T.closeSaved)
    } catch (e) {
      onLoi(e)
    } finally {
      setDang(false)
    }
  }

  async function moLai(d: DotDongCua) {
    try {
      const r = await cauHinh.moLaiNgayDong(branchId, { tuNgay: d.tuNgay, denNgay: d.denNgay })
      setVuong(null)
      onDoi(r.chiNhanh)
      onXong(T.closeReopened)
    } catch (e) {
      onLoi(e)
    }
  }

  return (
    <section className="the the-dem the-tiem-nghi">
      <div className="the-tieu-de">
        <div>
          <h2>{T.closeDaysTitle}</h2>
          <p>{T.closeDaysHint}</p>
        </div>
      </div>
      {ngayDong.length > 0 && (
        <ul className="ds-nghi">
          {ngayDong.map((d) => (
            <li key={`${d.tuNgay}-${d.denNgay}`}>
              <CalendarOff size={18} />
              {/* Ba mảnh xếp dòng theo bề ngang: "Nghỉ Tết" không bao giờ bị tách đôi, "còn n lịch…" xuống dòng riêng khi hẹp. */}
              <span className="nghi-chu">
                <b className="nghi-ngay">
                  {d.tuNgay === d.denNgay
                    ? ngayViet(d.tuNgay)
                    : `${ngayViet(d.tuNgay)} – ${ngayViet(d.denNgay)}`}
                </b>
                {d.lyDo !== null && <span className="chu-phu nghi-ly-do">{d.lyDo}</span>}
                {d.soLichVuong > 0 && (
                  <span className="chu-canh-bao nghi-con-lich">
                    {T.closeStillBusy(d.soLichVuong)}
                  </span>
                )}
              </span>
              <button
                type="button"
                className="nut nut-nhat nut-mo-lai"
                aria-label={T.closeReopen}
                onClick={() => void moLai(d)}
              >
                <X size={20} />
                <span className="chu-mo-lai">{T.closeReopenShort}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="hang-nghi">
        <div className="truong">
          <label className="nhan" htmlFor={`dong-tu-${branchId}`}>
            {T.offFrom}
          </label>
          <ONgay
            key={`tu-${lan}`}
            id={`dong-tu-${branchId}`}
            giaTri={tu}
            onDoi={setTu}
            goiYKhiDung={() => ''}
          />
        </div>
        <div className="truong">
          <label className="nhan" htmlFor={`dong-den-${branchId}`}>
            {T.offTo}
          </label>
          <ONgay
            key={`den-${lan}`}
            id={`dong-den-${branchId}`}
            giaTri={den}
            onDoi={setDen}
            goiY={T.offToHint}
            goiYKhiDung={() => ''}
          />
        </div>
      </div>
      <div className="truong">
        <label className="nhan" htmlFor={`dong-ly-do-${branchId}`}>
          {T.offReason}
        </label>
        <input
          id={`dong-ly-do-${branchId}`}
          className="o-nhap"
          value={lyDo}
          maxLength={100}
          placeholder={T.closeReasonExample}
          onChange={(e) => setLyDo(e.target.value)}
        />
      </div>
      {/* Nút PHỤ (viền), không phải nút chính xanh đặc: "Lưu chi nhánh" ngay phía trên mới là nút chính của màn. */}
      <div className="hang-nut-tiem-nghi">
        <button
          type="button"
          className="nut nut-vien nut-lon"
          disabled={tu === '' || dang}
          onClick={() => void dong()}
        >
          {dang ? T.closeDoing : T.closeDo}
        </button>
      </div>
      {vuong !== null && (
        <CanhBaoLichVuong
          loiDau={T.closeSavedBusy}
          hienMa={false}
          ds={vuong.ds}
          tong={vuong.tong}
          {...(onXemTrenLich === undefined
            ? {}
            : { onMo: (v: LichVuongNghi) => onXemTrenLich(v.batDau.slice(0, 10), v.branchId) })}
        />
      )}
    </section>
  )
}
